import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, Icon, Text, Tooltip, Well } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';

import * as styles from './GrowthCreditsStats.css';

const MOCK_BALANCE = 24550;
const MOCK_MEDIA_SPEND_VALUE = 49100;
const MOCK_INVOICE_DISCOUNT_VALUE = 24550;
const MOCK_CREDITS_EARNED = 36905;
const MOCK_CREDITS_SPENT = 26500;

interface IStatDisplayProps {
  label: string;
  value: string;
  tooltip?: string;
}

function StatDisplay({ label, value, tooltip }: Readonly<IStatDisplayProps>): JSX.Element {
  return (
    <div className={styles.statDisplay}>
      <Flex align={'center'} gap={'xs'}>
        <Text.DefaultMedium>{label}</Text.DefaultMedium>
        {tooltip && (
          <Tooltip title={tooltip}>
            <Icon icon={faInfoCircle} />
          </Tooltip>
        )}
      </Flex>
      <Flex align={'center'} gap={'xs'}>
        <span className={styles.statValue}>{value}</span>
        <span className={styles.coinIcon}>
          <GrowthCreditIcon />
        </span>
      </Flex>
    </div>
  );
}

function GrowthCreditsStats(): JSX.Element {
  const { t } = useTranslation();

  return (
    <Flex direction={'column'} gap={'sm'}>
      <Card contentClassName={styles.balanceCard} width={'full'}>
        <div className={styles.balanceRow}>
          <StatDisplay
            label={t('Balance available for use')}
            tooltip={t(
              'Growth Credits are automatically earned from donation form tips and applied to your marketing costs. No manual action needed.',
            )}
            value={MOCK_BALANCE.toLocaleString()}
          />
          <Well className={styles.well} layout={'vertical'} theme={'slate'}>
            <Text.DefaultHeadingMedium>{t('Media Spend Value')}</Text.DefaultHeadingMedium>
            <Text.DefaultRegular>${MOCK_MEDIA_SPEND_VALUE.toLocaleString()}</Text.DefaultRegular>
          </Well>
          <Well className={styles.well} layout={'vertical'} theme={'slate'}>
            <Text.DefaultHeadingMedium>{t('Invoice Discount Value')}</Text.DefaultHeadingMedium>
            <Text.DefaultRegular>
              ${MOCK_INVOICE_DISCOUNT_VALUE.toLocaleString()}
            </Text.DefaultRegular>
          </Well>
        </div>
      </Card>
      <div className={styles.bottomRow}>
        {/* empty divs added to prevent Card styling adding margin-top to direct siblings. */}
        <div>
          <Card contentClassName={styles.statCard} width={'full'}>
            <StatDisplay label={t('Credits earned')} value={MOCK_CREDITS_EARNED.toLocaleString()} />
          </Card>
        </div>
        <div>
          <Card contentClassName={styles.statCard} width={'full'}>
            <StatDisplay label={t('Credits spent')} value={MOCK_CREDITS_SPENT.toLocaleString()} />
          </Card>
        </div>
      </div>
    </Flex>
  );
}

export default GrowthCreditsStats;
